import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ReviewBadge } from "./ReviewBadge";
import { Pic } from "./Pic";
import { HEADLINE, HERO_EYEBROW_ITEMS, HERO_INTRO } from "../data/headline";
import { InquiryCTA } from "./PlanEventModal";
import { HeroStars } from "./HeroStar";
import { HeroBackdrop } from "./HeroBackdrop";

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } });

// Tilted polaroid photos in the side gutters (desktop), one per side on tablet.
const PHOTOS = [
  { src: "/images/event-7.jpg", alt: "Corporate team at a conference table holding up number one after winning a trivia round", pos: "left-[-2%] top-[22%] lg:left-[1%]", rot: "-6deg", dur: "11s", delay: "0s", tablet: true },
  { src: "/images/event-10.jpg", alt: "Three friends smiling and holding the Trivia Nation trophy at a private house party", pos: "left-[3%] bottom-[6%]", rot: "4deg", dur: "13s", delay: "2s" },
  { src: "/images/event-6.jpg", alt: "Winning team holding the Trivia Nation trophy and a Game Show Loco champions poster in front of the live scoreboard", pos: "right-[-2%] top-[20%] lg:right-[1%]", rot: "5deg", dur: "12s", delay: "1s", tablet: true },
  { src: "/images/event-8.jpg", alt: "Leadership team posing with the Trivia Nation trophy at a Brooks Rehabilitation leadership challenge", pos: "right-[3%] bottom-[8%]", rot: "-4deg", dur: "14s", delay: "3s" },
];

export const HeroCentered = () => (
  <section data-testid="home-hero" className="relative isolate overflow-hidden bg-white">
    <HeroStars />
    <HeroBackdrop />
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: "linear-gradient(to bottom, #fff 0%, #fff 60%, rgba(255,194,14,0.08) 75%, rgba(255,194,14,0.35) 100%)" }} />

    {PHOTOS.map((p, i) => (
      <div key={p.src} aria-hidden={!p.tablet} data-testid={`hero-side-photo-${i}`} className={`hero-float-slow absolute -z-[1] ${p.pos} ${p.tablet ? "hidden md:block" : "hidden lg:block"} w-[140px] xl:w-[190px] 2xl:w-[230px]`} style={{ "--dur": p.dur, "--delay": p.delay, "--rot": p.rot }}>
        <div className="hero-polaroid rounded-xl bg-white p-2 pb-6 shadow-[0_30px_60px_-25px_rgba(0,0,0,0.45)] ring-1 ring-black/5">
          <Pic src={p.src} alt={p.alt} sizes="230px" className="aspect-[4/3] w-full rounded-md object-cover" />
        </div>
      </div>
    ))}

    <div className="relative mx-auto flex max-w-5xl flex-col items-center px-5 pb-20 pt-16 text-center sm:px-8 lg:min-h-[82vh] lg:justify-center lg:py-24">
      <motion.p {...fade(0)} data-testid="hero-eyebrow" className="eyebrow mb-5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 !text-[11px] !tracking-[0.14em] sm:gap-x-3 sm:!text-sm sm:!tracking-[0.2em] md:whitespace-nowrap">
        {HERO_EYEBROW_ITEMS.map((item, i) => (
          <span key={item} className="inline-flex items-center gap-x-2 sm:gap-x-3">
            {i > 0 && <span aria-hidden className="text-[0.8em] leading-none text-en-yellow">★</span>}
            <span>{item}</span>
          </span>
        ))}
      </motion.p>
      <h1 data-testid="hero-headline" aria-label={`${HEADLINE.line1} ${HEADLINE.line2}`} className="hero-rise font-display text-[clamp(1.5rem,8.3vw,2.25rem)] leading-[1.05] text-black sm:text-5xl xl:text-6xl 2xl:text-7xl">
        <span className="block md:whitespace-nowrap">{HEADLINE.line1}</span>{" "}
        <span className="block text-en-teal whitespace-nowrap">
          <span className="type-line" style={{ "--chars": HEADLINE.line2.length }}>
            <span className="type-ghost" aria-hidden="true">{HEADLINE.line2}</span>
            <span className="type-text" data-testid="hero-typed">{HEADLINE.line2.replace(/Lifetime\.?$/, "")}<span className="gold-word" data-testid="hero-gold-word">Lifetime</span>{HEADLINE.line2.endsWith(".") ? "." : ""}</span>
          </span>
        </span>
      </h1>
      <motion.p {...fade(0.2)} data-testid="hero-intro" style={{ textWrap: "balance" }} className="mt-7 max-w-[46ch] text-center text-[15px] leading-[1.55] text-black/70 sm:text-base lg:max-w-none lg:text-[17px]">
        <span className="lg:whitespace-nowrap">{HERO_INTRO.line1}</span>{" "}<br className="hidden lg:inline" />
        <span className="lg:whitespace-nowrap">{HERO_INTRO.line2}</span>
      </motion.p>
      <motion.div {...fade(0.3)} className="mt-12 flex flex-wrap items-center justify-center gap-4">
        <InquiryCTA label="Plan Your Event" icon data-testid="hero-plan-event-btn" className="btn-primary !px-8 !py-4" />
        <Link to="/experiences" data-testid="hero-explore-btn" data-track="explore_experiences" className="btn-outline !px-8 !py-4">Explore Experiences</Link>
      </motion.div>
      <motion.div {...fade(0.4)} className="mt-8 flex justify-center">
        <ReviewBadge testId="hero-review-badge" />
      </motion.div>
      <div data-testid="hero-mobile-photos" className="mt-12 flex w-full justify-center gap-4 md:hidden">
        {PHOTOS.filter((p) => p.tablet).map((p) => (
          <div key={p.src} className="w-[44%] max-w-[180px] rounded-xl bg-white p-1.5 pb-4 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.4)] ring-1 ring-black/5" style={{ transform: `rotate(${p.rot})` }}>
            <Pic src={p.src} alt={p.alt} sizes="180px" className="aspect-[4/3] w-full rounded-md object-cover" />
          </div>
        ))}
      </div>
    </div>
  </section>
);
